import React, { useEffect, useRef, useCallback } from 'react'
import { Box, Container, Stack, Text, Center, Loader, Button, Alert } from '@mantine/core'
import { observer } from 'mobx-react-lite'
import { useStore } from '../app/stores/store'
import { IoAlert, IoRefresh } from 'react-icons/io5'
import { ShootCard, PostCard, BlogCard } from './FeedCards'
import type { ShootDto } from './FeedCards/ShootCard'
import type { PostDto } from './FeedCards/PostCard'
import type { BlogDto } from './FeedCards/BlogCard'
import type { FeedItem } from '../app/stores/slopFeedStore'
import { toAbsoluteMediaUrl } from '../app/api/resolveApiBaseUrl'

const SCROLL_NEAR_BOTTOM_PX = 500
const DEBOUNCE_MS = 200

function absMediaUrl (url?: string | null): string {
  return toAbsoluteMediaUrl(url ?? '')
}

function inferVideoFromUrl (url: string): boolean {
  if (!url) return true
  return /\.(mp4|webm|mov|m4v|mkv)(\?|$)/i.test(url) || /\/videos\//i.test(url)
}

function feedItemToShootCard (item: FeedItem): ShootDto {
  const mediaUrl = absMediaUrl(item.mediaUrl)
  const mt = (item.mediaType || '').toLowerCase()
  const isVideo =
    mt === 'video' ? true : mt === 'photo' ? false : inferVideoFromUrl(mediaUrl)
  const createdMs = new Date(item.createdAt).getTime()
  const expiresAt =
    item.expiresAt?.trim() ||
    new Date(createdMs + 24 * 60 * 60 * 1000).toISOString()
  const linked = [item.firearmName, item.cartridgeLabel].filter(Boolean).join(' · ')
  return {
    id: item.id,
    userId: item.userId,
    mediaUrl,
    mediaType: isVideo ? 'video' : 'image',
    textBoxesJson: item.textBoxesJson ?? '',
    createdAt: item.createdAt,
    expiresAt,
    userAvatar: item.userAvatarUrl ?? '',
    userName: (item.userDisplayName || 'Member').trim() || 'Member',
    likeCount: item.likeCount,
    commentCount: item.commentCount,
    linkedCaption: linked || undefined
  }
}

function feedItemToPostCard (item: FeedItem): PostDto {
  const videoUrl = absMediaUrl(item.mediaUrl)
  return {
    id: item.id,
    userId: item.userId,
    videoUrl,
    thumbnail: absMediaUrl(item.thumbnailUrl) || videoUrl,
    title: item.title || 'Post',
    createdAt: item.createdAt,
    userAvatar: item.userAvatarUrl ?? '',
    userName: (item.userDisplayName || 'Member').trim() || 'Member',
    likeCount: item.likeCount,
    commentCount: item.commentCount
  }
}

function feedItemToBlogCard (item: FeedItem): BlogDto {
  const author = (item.userDisplayName || 'Member').trim() || 'Member'
  return {
    id: item.id,
    userId: item.userId,
    title: item.title || 'Blog',
    excerpt: item.excerpt || item.description || '',
    cleverTags: item.tags ?? [],
    createdAt: item.createdAt,
    userAvatar: item.userAvatarUrl ?? '',
    userName: item.firearmName ? `${author} · ${item.firearmName}` : author,
    likeCount: item.likeCount,
    thumbnail: item.thumbnailUrl ? absMediaUrl(item.thumbnailUrl) : undefined
  }
}

/**
 * Mixed discover feed with infinite scroll (MobX store, GET /api/slop-feed).
 */
export const SlopFeed = observer(() => {
  const { slopFeedStore, userStore } = useStore()
  const observerTarget = useRef<HTMLDivElement>(null)
  const loadMoreTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!userStore.isLoggedIn) return
    void slopFeedStore.fetchFeedItems(slopFeedStore.defaultLimit, null)
    return () => {
      if (loadMoreTimeoutRef.current) clearTimeout(loadMoreTimeoutRef.current)
    }
  }, [userStore.isLoggedIn, slopFeedStore])

  const debouncedLoadMore = useCallback(() => {
    if (loadMoreTimeoutRef.current) clearTimeout(loadMoreTimeoutRef.current)
    loadMoreTimeoutRef.current = setTimeout(() => {
      loadMoreTimeoutRef.current = null
      const s = slopFeedStore
      if (s.isLoading || !s.hasMore || !s.nextCursor) return
      void s.fetchFeedItems(s.defaultLimit, s.nextCursor)
    }, DEBOUNCE_MS)
  }, [slopFeedStore])

  useEffect(() => {
    const el = observerTarget.current
    if (!el) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) debouncedLoadMore()
        })
      },
      {
        root: null,
        rootMargin: `${SCROLL_NEAR_BOTTOM_PX}px 0px`,
        threshold: 0.01
      }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [debouncedLoadMore, slopFeedStore.feedItems.length, slopFeedStore.hasMore, slopFeedStore.nextCursor])

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement
      const distanceFromBottom = doc.scrollHeight - window.innerHeight - window.scrollY
      if (distanceFromBottom <= SCROLL_NEAR_BOTTOM_PX) debouncedLoadMore()
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [debouncedLoadMore])

  if (!userStore.isLoggedIn) {
    return (
      <Container size="md">
        <Center style={{ minHeight: '50vh' }}>
          <Stack align="center" spacing="md">
            <Text size="lg" weight={600}>
              Please log in to view the feed
            </Text>
            <Button onClick={() => (window.location.href = '/login')}>Go to Login</Button>
          </Stack>
        </Center>
      </Container>
    )
  }

  if (slopFeedStore.isLoading && slopFeedStore.feedItems.length === 0) {
    return (
      <Container size="md">
        <Center style={{ minHeight: '50vh' }}>
          <Stack align="center" spacing="md">
            <Loader size="lg" />
            <Text color="dimmed">Loading feed...</Text>
          </Stack>
        </Center>
      </Container>
    )
  }

  return (
    <Container size="md">
      <Stack spacing="md" py="md">
        {slopFeedStore.error && (
          <Alert
            icon={<IoAlert size={16} />}
            color="red"
            title="Error loading feed"
            onClose={() => slopFeedStore.clearError()}
            withCloseButton
          >
            {slopFeedStore.error}
            <Button
              variant="subtle"
              size="xs"
              mt="sm"
              onClick={() => void slopFeedStore.retry()}
              leftIcon={<IoRefresh size={14} />}
            >
              Retry
            </Button>
          </Alert>
        )}

        {slopFeedStore.feedItems.length === 0 &&
          !slopFeedStore.isLoading &&
          slopFeedStore.initialLoadSucceeded && (
            <Center style={{ minHeight: '40vh' }}>
              <Stack align="center" spacing="sm">
                <Text size="lg" weight={600}>
                  No posts yet
                </Text>
                <Text size="sm" color="dimmed">
                  Check back later for new content
                </Text>
                <Button
                  variant="light"
                  onClick={() => void slopFeedStore.fetchFeedItems(slopFeedStore.defaultLimit, null)}
                  leftIcon={<IoRefresh size={16} />}
                >
                  Refresh
                </Button>
              </Stack>
            </Center>
        )}

        {slopFeedStore.feedItems.map((item) => (
          <FeedItemCard key={`${item.type}-${item.id}`} item={item} />
        ))}

        <Box ref={observerTarget} py="md" aria-hidden />

        {slopFeedStore.isLoading && slopFeedStore.feedItems.length > 0 && (
          <Center py="md">
            <Stack align="center" spacing="sm">
              <Loader size="sm" />
              <Text size="sm" color="dimmed">
                Loading more...
              </Text>
            </Stack>
          </Center>
        )}

        {!slopFeedStore.hasMore && slopFeedStore.feedItems.length > 0 && !slopFeedStore.isLoading && (
          <Center py="lg">
            <Text size="sm" color="dimmed" ta="center">
              No more content
            </Text>
          </Center>
        )}
      </Stack>
    </Container>
  )
})

SlopFeed.displayName = 'SlopFeed'

interface FeedItemCardProps {
  item: FeedItem;
}

function shareUrl (url: string) {
  if (navigator.share) {
    void navigator.share({ title: 'GunSlop', url }).catch(() => undefined)
  } else {
    void navigator.clipboard.writeText(url).catch(() => undefined)
  }
}

const FeedItemCard: React.FC<FeedItemCardProps> = observer(({ item }) => {
  const { slopFeedStore } = useStore()
  const origin = typeof window !== 'undefined' ? window.location.origin : ''

  if (item.type === 'Shoot') {
    return (
      <ShootCard
        shoot={feedItemToShootCard(item)}
        onShare={(id) => shareUrl(`${origin}/slop-feed/shoot/${id}`)}
        onDeleted={(id) => slopFeedStore.removeFeedItemById(id)}
      />
    )
  }

  if (item.type === 'Blog') {
    return (
      <BlogCard
        blog={feedItemToBlogCard(item)}
        onShare={(id) => shareUrl(`${origin}/blog/${id}`)}
      />
    )
  }

  if (item.type === 'Post') {
    return (
      <PostCard post={feedItemToPostCard(item)} onShare={(id) => shareUrl(`${origin}/slop-feed/${id}`)} />
    )
  }

  return null
})

export default SlopFeed
